/**
 * License.ts (AbstractLicensingBackend)
 *
 * Copyright © 2020 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Timothy Fadayini, 2020
 *
 * @file License.ts
 * @author Timothy Fadayini
 * @copyright 2020 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import { BASE_API_URL } from '../config';
import { mapSortingFields, mapFilterFields } from '../Utils/Pagination';
import { ILicense } from '@abstract/abstractwebcommon-shared/interfaces/license/license';
import {
  IAPIEntityResponse,
  PaginatedAPIEntityResponse
} from '@abstract/abstractwebcommon-shared/interfaces/api';
import { HttpClient } from '@abstract/abstractwebcommon-client/utils/HttpClient';
import { createLogApi } from './Log';
import { IEntitlement } from '@abstract/abstractwebcommon-shared/interfaces/license/entitlement';
import { LocalStorage } from '@abstract/abstractwebcommon-client/utils/sharedLocalStorage';

const httpClient = new HttpClient(BASE_API_URL, createLogApi);

/**
 * Create or update a license
 * @param license License information
 * @returns Promise
 */
export const createOrUpdate = async (license: any): Promise<IAPIEntityResponse<ILicense>> => {
  // Send only entitlementUUID(s) in request payload
  const requestPayload: ILicense = { ...license };
  if (license.entitlements) {
    requestPayload.entitlements = license.entitlements.map((entitlement: IEntitlement) => ({
      entitlementUUID: entitlement.entitlementUUID
    }));
  }

  if (license.licenseUUID) {
    return httpClient.post<ILicense>(`/license/${license.licenseUUID}/update`, requestPayload);
  }

  return httpClient.post<ILicense>(`/license/create`, requestPayload);
};

/**
 * Gets licenses
 * @param id license id
 * @param skip Skip
 * @param limit Limit
 * @param sort Sort
 * @param sortOrder Sort order
 * @param data Data of filter values
 * @param searchTerm Search term to find
 * @returns Promise
 */
export const read = async (
  searchTerm: string,
  id?: string | null,
  skip = 0,
  limit = 10,
  sort = 'created',
  sortOrder = 'DESC',
  data = {}
): Promise<PaginatedAPIEntityResponse<ILicense>> => {
  const url: string = `/licenses/all`;
  const clientURL: string = `/licenses/all/client/${LocalStorage.getXUserUUID()}`; /**< Client license route */
  const isAdmin: boolean = LocalStorage.getAdmin();

  if (id) {
    return httpClient.get<ILicense>(`${url}/${id}`);
  } else {
    const mappedSort: string = mapSortingFields(sort, sortOrder);
    const mappedFilter: string = mapFilterFields(data, [
      'active',
      'id',
      'licenseType'
    ]); /** Remap operator of active filter as 'IN' */

    return httpClient.get<ILicense>(
      `${
        isAdmin ? url : clientURL
      }?filter=${mappedFilter}&skip=${skip}&limit=${limit}&sort=${mappedSort}&searchTerm=${encodeURIComponent(
        searchTerm ? searchTerm : ''
      )}`
    );
  }
};

/**
 * Deletes license(s) by licenseUUIDs
 * @param payload licenseUUIDs
 * @returns Promise
 */
export const remove = (payload: string[]): Promise<IAPIEntityResponse<void>> => {
  return httpClient.post(`/licenses/delete`, { licenseUUIDs: payload });
};

/**
 * Fetches all license templates
 * @param filter Filter
 * @returns Promise
 */
export const allLicenseTemplates = (filter: string): Promise<IAPIEntityResponse<ILicense[]>> => {
  return httpClient.get<ILicense[]>(`/license/templates?filter=${filter || ''}`);
};

/**
 * Fetches total licenses count
 * @returns Promise<ILicense>
 */
export const getTotalLicenseCount = (): Promise<IAPIEntityResponse<ILicense>> => {
  const isAdmin: boolean = LocalStorage.getAdmin();
  const endpointTypeName: string = isAdmin ? 'admin' : 'client';
  return httpClient.get<ILicense>(`/licenses/${endpointTypeName}/get/count`);
};

/**
 * Remove Eula entitlement from user license
 * @returns Promise
 */
export const removeEulaEntitlementFromUserLicense = (): Promise<IAPIEntityResponse<void>> => {
  return httpClient.get<void>(`/license/eula/accept`);
};

/**
 * Remove SLA entitlement from user license
 * @returns Promise
 */
export const removeSLAEntitlementFromUserLicense = (): Promise<IAPIEntityResponse<void>> => {
  return httpClient.get<void>(`/license/sla/accept`);
};

/**
 * Get License information by license UUID
 * @param licenseUUID License UUID
 * @returns Promise
 */
export const getLicenseByUUID = (licenseUUID: string): Promise<IAPIEntityResponse<ILicense>> => {
  return httpClient.get<ILicense>(`/license/${licenseUUID}`);
};

/**
 * Stats for one auto-issued license segment. The pioneer segment aggregates
 * pioneer-origin and free-entitlement licenses (the same population).
 */
export interface IAutomaticLicenseTypeStatistics {
  configured: boolean /**< False if the template UUID is unset or has no match. */;
  projectName: string | null /**< Configured template name (UI hint only). */;
  stats: {
    total: number /**< Lifetime issuance, includes soft-deleted. */;
    active: number /**< Currently valid (licenseEndDate > now). */;
    inRange: number /**< Count issued within the selected range. */;
    rangeDays: number | null /**< Window the response was computed for; null = all time. */;
    bucketing: 'day' | 'week' | 'month' /**< Series granularity. */;
    series: { date: string; count: number }[] /**< One entry per bucket, in order. `date` is the bucket start. */;
  };
}

export interface IAutomaticLicenseStatistics {
  pioneer: IAutomaticLicenseTypeStatistics;
  trial: IAutomaticLicenseTypeStatistics;
  paid: IAutomaticLicenseTypeStatistics /**< Everything that isn't pioneer or trial. */;
}

/**
 * Fetches pioneer/trial license issuance statistics for the admin dashboard.
 *
 * @param rangeDays Number of days to include in the range, or 'all' for full history.
 *   Defaults server-side to 30. Server caps at 3650.
 */
export const getAutomaticLicenseStatistics = (
  rangeDays?: number | 'all'
): Promise<IAPIEntityResponse<IAutomaticLicenseStatistics>> => {
  const query: string =
    rangeDays === undefined ? '' : `?days=${encodeURIComponent(String(rangeDays))}`;
  return httpClient.get<IAutomaticLicenseStatistics>(
    `/license/automatic/statistics${query}`
  );
};

/**
 * New-vs-returning statistics for one entity level (users or machines) within a
 * segment. Computed from the append-only activation event log.
 */
export interface IActivationLevelStatistics {
  total: number /**< Distinct entities ever seen in the segment. */;
  active: number /**< Distinct entities with any event inside the range. */;
  newInRange: number /**< Distinct entities first seen inside the range. */;
  returningInRange: number /**< Active in range but first seen before it. */;
  rangeDays: number | null /**< Window the response was computed for; null = all time. */;
  bucketing: 'day' | 'week' | 'month' /**< Series granularity. */;
  series: {
    date: string;
    newCount: number;
    returningCount: number;
  }[] /**< One entry per bucket, in order. `date` is the bucket start. */;
}

/**
 * Activation stats for one segment (overall, pioneer or trial), carrying both
 * user- and machine-level breakdowns.
 */
export interface IActivationSegmentStatistics {
  configured?: boolean /**< Pioneer/trial only: false if the template UUID is unset. */;
  projectName?: string | null /**< Pioneer/trial only: configured template name (UI hint). */;
  user: IActivationLevelStatistics;
  machine: IActivationLevelStatistics;
}

export interface IActivationStatistics {
  overall: IActivationSegmentStatistics;
  pioneer: IActivationSegmentStatistics;
  trial: IActivationSegmentStatistics;
  paid: IActivationSegmentStatistics /**< Everything that isn't pioneer or trial. */;
}

/** Entity level the dashboard is currently showing. */
export type ActivationStatisticsLevel = 'user' | 'machine';

/**
 * Fetches activation statistics (new vs. returning users) for the admin dashboard.
 *
 * @param rangeDays Number of days to include in the range, or 'all' for full history.
 *   Defaults server-side to 30. Server caps at 3650.
 */
export const getActivationStatistics = (
  rangeDays?: number | 'all'
): Promise<IAPIEntityResponse<IActivationStatistics>> => {
  const query: string =
    rangeDays === undefined ? '' : `?days=${encodeURIComponent(String(rangeDays))}`;
  return httpClient.get<IActivationStatistics>(
    `/license/activation/statistics${query}`
  );
};
